import React, { useEffect, useMemo, useRef } from 'react';
import { Animated, Easing, Platform, Pressable, Text, View } from 'react-native';
import { ChevronRight, Crosshair, Trophy } from 'lucide-react-native';
import { useTheme } from '../../../src/theme/useTheme';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { CountryFlag } from '../CountryFlag/CountryFlag';
import {
  getWcBrand,
  getWorldCupScreen,
  isNativeIosAppStoreBranding,
} from '../../../src/worldcup/helpers/wcBrand.helper';
import { WORLD_CUP_WELCOME } from '../../../src/worldcup/worldcup.constants';
import {
  WORLD_CUP_WELCOME_ANIM,
  WORLD_CUP_WELCOME_CIRCLE_COUNT,
} from './WorldCupWelcomeScreen.constants';
import { getCircleBaseStyles, getWelcomeSplashPhaseStyles } from './WelcomeSplashPhase.styles';

export interface WelcomeSplashPhaseProps {
  isExiting: boolean;
  onNext: () => void;
  testID?: string;
}

export const WelcomeSplashPhase: React.FC<WelcomeSplashPhaseProps> = ({
  isExiting,
  onNext,
  testID,
}) => {
  const theme = useTheme();
  const useIosAppStoreBranding = isNativeIosAppStoreBranding();
  const styles = useMemo(
    () => getWelcomeSplashPhaseStyles(theme, useIosAppStoreBranding),
    [theme, useIosAppStoreBranding],
  );
  const circleBaseStyles = useMemo(() => getCircleBaseStyles(theme), [theme]);
  const worldCupScreen = useMemo(() => getWorldCupScreen(), []);
  const circleAnims = useRef(
    Array.from({ length: WORLD_CUP_WELCOME_CIRCLE_COUNT }, () => new Animated.Value(0)),
  ).current;

  useEffect(() => {
    const timeouts: ReturnType<typeof setTimeout>[] = [];
    const anims: Animated.CompositeAnimation[] = [];

    circleAnims.forEach((anim, i) => {
      const duration =
        WORLD_CUP_WELCOME_ANIM.CIRCLE_BASE_DURATION_MS +
        i * WORLD_CUP_WELCOME_ANIM.CIRCLE_DURATION_STEP_MS;
      const t = setTimeout(() => {
        const loopAnim = Animated.loop(
          Animated.sequence([
            Animated.timing(anim, {
              toValue: 1,
              duration,
              easing: Easing.inOut(Easing.sin),
              useNativeDriver: true,
            }),
            Animated.timing(anim, {
              toValue: 0,
              duration,
              easing: Easing.inOut(Easing.sin),
              useNativeDriver: true,
            }),
          ]),
        );
        loopAnim.start();
        anims.push(loopAnim);
      }, i * WORLD_CUP_WELCOME_ANIM.CIRCLE_DELAY_STEP_MS);
      timeouts.push(t);
    });

    return () => {
      timeouts.forEach(clearTimeout);
      anims.forEach((a) => a.stop());
    };
  }, [circleAnims]);

  return (
    <View style={styles.container} testID={testID}>
      <View style={styles.circlesLayer} pointerEvents="none">
        {circleAnims.map((anim, i) => {
          const scale = anim.interpolate({ inputRange: [0, 1], outputRange: [1, 1.2] });
          const opacity = anim.interpolate({ inputRange: [0, 1], outputRange: [0.08, 0.22] });
          return (
            <Animated.View
              key={i}
              style={[circleBaseStyles[i], { transform: [{ scale }], opacity }]}
            />
          );
        })}
      </View>

      <View style={styles.iconWrap} testID="worldcup-welcome-splash-hero">
        {useIosAppStoreBranding ? (
          <Crosshair
            size={56}
            color={theme.white}
            strokeWidth={2.2}
            accessibilityLabel="Predictions"
          />
        ) : (
          <Trophy
            size={56}
            color={theme.white}
            strokeWidth={2.2}
            accessibilityLabel="Trophy"
          />
        )}
        <Animated.View style={styles.iconRing} />
      </View>

      {useIosAppStoreBranding ? (
        <Text style={styles.title}>{getWcBrand().NAME}</Text>
      ) : (
        <>
          <Text style={styles.title}>{worldCupScreen.TITLE}</Text>
          <Text style={styles.subtitle}>{WORLD_CUP_WELCOME.SPLASH.SUBTITLE}</Text>
        </>
      )}
      <Text style={styles.tagline}>{WORLD_CUP_WELCOME.SPLASH.TAGLINE}</Text>

      {!useIosAppStoreBranding && (
        <View style={styles.hostFlagsRow}>
          {WORLD_CUP_WELCOME.SPLASH.HOST_NATIONS.map((nation) => (
            <View key={nation.COUNTRY_CODE} style={styles.hostFlagSlot}>
              <CountryFlag
                countryCode={nation.COUNTRY_CODE}
                size="default"
                accessibilityLabel={nation.ACCESSIBILITY_LABEL}
              />
            </View>
          ))}
        </View>
      )}

      <Pressable
        accessibilityRole="button"
        accessibilityLabel={WORLD_CUP_WELCOME.SPLASH.CTA}
        disabled={isExiting}
        onPress={onNext}
        style={({ pressed, hovered }) => [
          styles.button,
          Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
          pressed && !isExiting && styles.buttonPressed,
          hovered && Platform.OS === 'web' && styles.buttonHovered,
        ]}
      >
        <Text style={styles.buttonLabel}>{WORLD_CUP_WELCOME.SPLASH.CTA}</Text>
        <ChevronRight size={16} color={theme.onPrimary} strokeWidth={2.5} />
      </Pressable>
    </View>
  );
};
